import React from "react";
import styled from "styled-components";
import media from 'styled-media-query';
import logo from 'components/shared/LogoImage/logo-white.png';
import { Icon } from "components/shared";

const FooterContainer = styled.footer`
  background-color: #1e1e1e;
  padding: 0 0.625rem;
`;

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 3.125rem 2.5rem;
  margin: 0 auto;
  ${media.greaterThan('medium')`
    max-width: 73.125rem;
  `}
`;

const FooterTop = styled.div`
  display: flex;
  align-items: start;
  justify-content: start;

  @media screen and (max-width: 75rem) {
    flex-direction: column;
  }
`;

const FooterLeft = styled.div`
  padding-right: 6.6875rem;
  position: relative;

  @media screen and (max-width: 75rem) {
    padding-right: 0;
    &::after {
      content: none;
    }
  }
`;

const Logo = styled.a`
  padding: 0 0.625rem;
  transition: all 0.25s ease;
  &:focus {
    opacity: 0.85;
  }
`;

const Text = styled.p`
  font-size: 0.875rem;
  line-height: 1.375rem;
  padding: 0 0.625rem;
  color: #fff;
  margin-bottom: ${props => (props.header ? "1rem" : "0")};
  font-weight: ${props => (props.header ? "700" : "300")};
`;

const FooterUnder = styled.p`
  font-size: 0.875rem;
  line-height: 1.375rem;
  font-weight: 300;
  color: #fff;
  margin: 0;
  padding: 0 0.625rem;
  margin-top: 1.5rem;
  margin-bottom: 0;
  @media (max-width: 1200px) {
    margin-top: 0;
    margin-bottom: 0;
  }
  ${media.greaterThan('medium')`   
    margin-top: 0;
    margin-bottom: 0;
  `}
`;

const Link = styled.a`
  position: relative;
  font-size: 0.875rem;
  padding: 0 0.625rem;
  text-decoration: none;
  font-weight: 300;
  color: #fff;
  &:hover,
  &:focus,
  &:active {
    text-decoration: underline;
  }
  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 0.125rem;
    background-color: #fff;
    opacity: 0.2;
    @media (max-width: 1200px) {
    display: none;
  }
  }
`;

const LogoImg = styled.img`
  max-width: 250px;
  vertical-align: middle;
`;

const FooterBottom = styled.div`
  padding: 0;
`;

const FooterBottomTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 0;
  position: relative;
  margin: 3rem 0 1.5rem 0;

  &::after,
  &::before {
    content: "";
    position: absolute;
    left: 0;
    width: 100%;
    height: 0.125rem;
    background-color: #fff;
    opacity: 0.2;
  }

  &::after {
    top: 0;
  }

  &::before {
    bottom: 0;
  }

  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: start;
    padding: 2.25rem 0;
    margin-top: 0;
  }
`;

const Links = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: start;
    gap: 1rem;
  }
`;

const Socials = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;

  @media screen and (max-width: 75rem) {
    justify-content: end;
    margin-top: 2.25rem;
  }
`;

const SocialLink = styled.a`
  position: relative;
  margin: 0 0.5rem;
  transition: all 0.25s ease;
  color: #BEC8AD

  &:hover,
  &:focus,
  &:active {
    svg {
      fill: #fff;
    }
  }

  &:last-child {
    margin-right: 0;
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <Container>
        <FooterTop>
          <FooterLeft>
            <Logo href="/">
              <LogoImg
                src={logo}
                alt="LendingOne White Logo"
                title="LendingOne"
              />
            </Logo>
          </FooterLeft>
        </FooterTop>
        <FooterBottom>
          <FooterBottomTop>
            <Links>
              <Link target="_blank" href="https://lendingone.com/privacy-policy/">Privacy Policy</Link>
              <Text>Copyright © {new Date().getFullYear()} LendingOne</Text>
            </Links>
            <Socials>
              <SocialLink
                target="_blank"
                href="https://www.linkedin.com/company/lendingone-llc"
                aria-label="LinkedIn Footer Link"
              >
                <Icon width="1.5625" icon="linkedin" />
              </SocialLink>
              <SocialLink
                target="_blank"
                href="https://www.facebook.com/LendingOne/"
                aria-label="Facebook Footer Link"
              >
                <Icon width="1.5625" icon="facebook_new" />
              </SocialLink>
              <SocialLink
                target="_blank"
                href="https://www.youtube.com/@LendingOne"
                aria-label="Youtube Footer Link"
              >
                <Icon width="1.5625" icon="youtube" />
              </SocialLink>
              <SocialLink
                target="_blank"
                href="https://www.instagram.com/lending_one/"
                aria-label="Instagram Footer Link"
              >
                <Icon width="1.5625" icon="instagram" />
              </SocialLink>
            </Socials>
          </FooterBottomTop>
        </FooterBottom>
        <FooterUnder>Loans made under LendingOne, LLC (NMLS ID # 1508627) pursuant to AZ Mortgage Banker License 0944181, CA – DFPI Financing Law License 60DBO-58915, ID Mortgage Broker/Lender License MBL-2081508627, MN Residential Mortgage Originator License MN-MO-1508627, OR Mortgage Lending License ML-5529, UT-DRE Mortgage Entity License 12767077, and VT Commercial Lender License 1508627 CLL. LendingOne, LLC currently does not lend in the following states: Alaska, Nevada, North Dakota, and South Dakota. LendingOne, LLC is licensed or exempt from licensing in all other states. Loans only apply to residential, non-owner occupied properties. Rates, terms and conditions offered only to qualified borrowers, may vary upon loan product, deal structure, property state or other applicable considerations, and are subject to change at any time without notice, shall only constitute a general, non-binding expression of interest on the part of LendingOne, LLC, do not create any legally binding commitment or obligation on the part of LendingOne, LLC, and are expressly subject to LendingOne, LLC’s credit, legal and investment approval process. Closing times are in business days and commence upon receipt of appraisal payment and satisfaction of borrower conditions. Closing times may be delayed due to appraiser property access limitations or availability in certain markets.</FooterUnder>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
